import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function DurbanvillePage() {
  useEffect(() => {
    document.title = "Durbanville Painters Cape Town | Professional Painting Services | Quality Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Durbanville properties. Expert interior & exterior painting for homes and businesses in Durbanville, Northern Suburbs, Cape Town.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Durbanville painters, Northern Suburbs painting, residential painting, Durbanville homes, Cape Town painters');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/durbanville`);
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Durbanville Painters - Professional Painting Services",
      "description": "Professional painting services for Durbanville properties with quality finishes and expert craftsmanship.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/durbanville`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Durbanville",
        "description": "Professional painting services for Durbanville residential and commercial properties.",
        "areaServed": "Durbanville, Cape Town",
        "serviceType": "Professional Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Durbanville')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Residential Painting',
      description: 'Complete interior and exterior painting for Durbanville homes. From established estates to new developments, we deliver quality finishes that enhance your property.',
      price: 'From R8,000',
      features: ['Interior painting', 'Exterior painting', 'Color consultation', 'Quality materials', 'Professional finish']
    },
    {
      icon: 'ri-building-line',
      title: 'Commercial Painting',
      description: 'Professional painting for Durbanville businesses, offices, and commercial properties. Flexible scheduling and efficient service to minimize disruption.',
      price: 'From R12,000',
      features: ['Office painting', 'Retail spaces', 'Commercial complexes', 'After-hours available', 'Quick completion']
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Specialized Services',
      description: 'Expert roof painting, waterproofing, and protective coatings for Durbanville properties. Long-lasting protection against the elements.',
      price: 'From R6,000',
      features: ['Roof painting', 'Waterproofing', 'Protective coatings', 'Weather resistance', 'Durability focus']
    }
  ];

  const features = [
    {
      title: 'Local Expertise',
      description: 'Extensive experience serving Durbanville and the Northern Suburbs. We understand local property styles, climate conditions, and community standards.'
    },
    {
      title: 'Quality Materials',
      description: 'Premium paints from Dulux and Plascon specifically selected for Durbanville\'s climate. Ensuring beautiful, long-lasting finishes for your property.'
    },
    {
      title: 'Professional Service',
      description: 'Experienced craftsmen delivering quality workmanship with attention to detail. Clean, efficient work practices and respect for your property.'
    },
    {
      title: 'Reliable & Punctual',
      description: 'Committed to timelines and professional standards. We arrive on schedule, work efficiently, and complete projects as promised.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Free Consultation',
      description: 'Comprehensive assessment of your Durbanville property with color consultation and detailed quotation. We discuss your needs and provide expert recommendations.'
    },
    {
      step: 2,
      title: 'Surface Preparation',
      description: 'Thorough preparation including cleaning, repairs, and priming. Proper preparation ensures optimal results and long-lasting finishes.'
    },
    {
      step: 3,
      title: 'Expert Application',
      description: 'Professional painting using quality materials and proven techniques. Our skilled team ensures even coverage and beautiful finishes.'
    },
    {
      step: 4,
      title: 'Quality Inspection',
      description: 'Detailed final inspection and walkthrough. We ensure complete satisfaction and address any concerns before project completion.'
    }
  ];

  const testimonials = [
    {
      name: 'Johan Pretorius',
      property: 'Durbanville Estate',
      rating: 5,
      text: 'Excellent work on our home. The team was professional, punctual, and the quality exceeded expectations. Our house looks fantastic and we\'ve received many compliments from neighbors.',
      image: 'https://public.readdy.ai/ai/img_res/7fca4978d5204c40ba780a777d16467a.jpg'
    },
    {
      name: 'Susan van Wyk',
      property: 'Family Home',
      rating: 5,
      text: 'Outstanding service from start to finish. They were respectful, clean, and delivered beautiful results. Highly recommend for anyone in Durbanville looking for quality painting.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20homeowner%20at%20Durbanville%20family%20home%2C%20professionally%20painted%20interior%2C%20satisfied%20client%20portrait%2C%20quality%20residential%20property&width=80&height=80&seq=durbanville2&orientation=squarish'
    },
    {
      name: 'Mark Johnson',
      property: 'Commercial Office',
      rating: 5,
      text: 'Professional service for our Durbanville office. They worked efficiently, minimized disruption, and delivered excellent results. Great value and highly professional throughout.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20owner%20at%20Durbanville%20commercial%20property%2C%20newly%20painted%20office%20building%2C%20satisfied%20customer%2C%20quality%20commercial%20painting&width=80&height=80&seq=durbanville3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Modern Estate Home',
      description: 'Complete exterior and interior painting for a contemporary Durbanville estate home. Premium finishes with modern color palette enhancing architectural features.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20estate%20home%20in%20Durbanville%20Northern%20Suburbs%2C%20freshly%20painted%20white%20exterior%2C%20contemporary%20architecture%2C%20professional%20painting%20work%2C%20quality%20finish&width=400&height=300&seq=durbanville4&orientation=landscape'
    },
    {
      title: 'Family Residence',
      description: 'Full interior transformation with fresh colors and quality finishes. Created warm, inviting spaces for this Durbanville family home.',
      image: 'https://readdy.ai/api/search-image?query=Durbanville%20family%20residence%2C%20newly%20painted%20exterior%2C%20residential%20property%2C%20professional%20painting%20finish%2C%20Northern%20Suburbs%20home&width=400&height=300&seq=durbanville5&orientation=landscape'
    },
    {
      title: 'Commercial Complex',
      description: 'Multi-unit commercial project with coordinated exterior scheme. Efficient completion with minimal disruption to business operations.',
      image: 'https://readdy.ai/api/search-image?query=Durbanville%20commercial%20complex%2C%20professional%20exterior%20painting%2C%20business%20property%2C%20modern%20commercial%20development%2C%20quality%20finish%20work&width=400&height=300&seq=durbanville6&orientation=landscape'
    },
    {
      title: 'Roof Restoration',
      description: 'Complete roof painting and waterproofing project. Enhanced protection and curb appeal for this Durbanville property with weather-resistant coatings.',
      image: 'https://readdy.ai/api/search-image?query=Durbanville%20home%20with%20professionally%20painted%20roof%2C%20residential%20property%2C%20roof%20restoration%20work%2C%20Northern%20Suburbs%20quality%20painting&width=400&height=300&seq=durbanville7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20residential%20property%20in%20Durbanville%20Northern%20Suburbs%2C%20painting%20equipment%2C%20scaffolding%2C%20quality%20suburban%20home%2C%20sunny%20day%2C%20Cape%20Town%20area&width=1920&height=1080&seq=durbanville_hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Durbanville
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Quality painting services for Durbanville homes and businesses. Expert craftsmanship and reliable service in the Northern Suburbs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Durbanville Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Trusted Painting Services in Durbanville
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Durbanville is a thriving suburb in Cape Town's Northern Suburbs, known for its wine farms, excellent schools, and quality residential developments. This family-friendly area combines suburban comfort with easy access to Cape Town's amenities.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our painting team has been serving Durbanville for many years, building a strong reputation for quality workmanship, reliability, and excellent customer service. We understand the local community and deliver painting services that meet Durbanville's high standards.
              </p>
              <p className="text-lg text-gray-600">
                From established family homes to new developments and commercial properties, we provide professional painting services that enhance your property's appearance and protect it for years to come.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Durbanville%20residential%20area%20Northern%20Suburbs%2C%20quality%20homes%2C%20suburban%20setting%2C%20family-friendly%20Cape%20Town%20suburb%2C%20wine%20farms%20backdrop&width=600&height=400&seq=durbanville8&orientation=landscape"
                alt="Durbanville Properties"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Durbanville Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting solutions for residential and commercial properties throughout Durbanville and the Northern Suburbs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Durbanville Painters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional service, quality materials, and expert craftsmanship for every Durbanville painting project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring quality results and complete satisfaction for every Durbanville project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Durbanville Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent painting projects across Durbanville residential and commercial properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied Durbanville property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Durbanville Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Durbanville's trusted painting professionals. Get a free quote and experience quality workmanship that enhances your property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
